import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from '../ui/dropdown-menu';
import { 
  Shield, 
  AlertOctagon, 
  Wallet, 
  LogOut, 
  User, 
  ChevronDown,
  Check,
  X,
  Coins,
  AlertTriangle,
  ArrowRightLeft,
  Gem
} from 'lucide-react';
import WalletConnectModal from '../WalletConnectModal';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header({ onPanicStop, panicMode }) {
  const { user, wallets, logout } = useAuth();
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isPanicLoading, setIsPanicLoading] = useState(false);
  const [creditInfo, setCreditInfo] = useState({ credits: 0, glabs_balance: 0, tier: 'STRATEGY', low_balance: false });
  const [showGlabsPanel, setShowGlabsPanel] = useState(false);
  const [panelTab, setPanelTab] = useState('buy'); // buy, convert, credits
  const [buyAmount, setBuyAmount] = useState('');
  const [convertAmount, setConvertAmount] = useState('');
  const [convertCurrency, setConvertCurrency] = useState('USDC');
  const [glabsToCredits, setGlabsToCredits] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCredits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/credits/balance`);
      setCreditInfo(data);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCredits();
    const interval = setInterval(fetchCredits, 10000);
    return () => clearInterval(interval);
  }, [fetchCredits]);

  const handlePanicStop = async () => {
    setIsPanicLoading(true);
    try {
      if (panicMode) {
        await axios.post(`${API_URL}/api/panic/reset`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/panic/stop`, {}, { withCredentials: true });
      }
      onPanicStop();
    } catch (error) {
      console.error('Panic action failed:', error);
    }
    setIsPanicLoading(false);
  };

  const handleBuyGlabs = async () => {
    if (!buyAmount || parseFloat(buyAmount) <= 0) return;
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/glabs/buy`, { usdt_amount: parseFloat(buyAmount) });
      toast.success(data.message);
      setCreditInfo(prev => ({ ...prev, glabs_balance: data.glabs_balance }));
      setBuyAmount('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Purchase failed'); }
    setActionLoading(false);
  };

  const handleConvertCrypto = async () => {
    if (!convertAmount || parseFloat(convertAmount) <= 0) return;
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/glabs/convert-crypto`, {
        currency: convertCurrency, amount: parseFloat(convertAmount),
      });
      toast.success(data.message);
      setCreditInfo(prev => ({ ...prev, glabs_balance: data.glabs_balance }));
      setConvertAmount('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
    setActionLoading(false);
  };

  const handleGlabsToCredits = async () => {
    if (!glabsToCredits || parseFloat(glabsToCredits) <= 0) return;
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/glabs/to-credits`, { glabs_amount: parseFloat(glabsToCredits) });
      toast.success(data.message);
      setCreditInfo(prev => ({ ...prev, credits: data.credits, glabs_balance: data.glabs_balance, low_balance: data.credits < 5 }));
      setGlabsToCredits('');
      fetchCredits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
    setActionLoading(false);
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const tierColors = { STRATEGY: '#00CED1', HARMONY: '#D4A017', POWER: '#E8520F' };
  const tierColor = tierColors[creditInfo.tier] || '#00CED1';
  const cryptoRates = { SOL: 150, ETH: 3000, USDC: 1 };

  return (
    <>
      <header className="glass-header sticky top-0 z-50 px-6 py-3" data-testid="header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#E8520F]" />
            <span className="text-xl font-bold font-['Outfit'] text-white">GauriLabs <span className="text-[#E8520F]">Commander</span></span>
          </div>

          <div className="flex items-center gap-3">
            {/* GLabs + Credits Bar */}
            <div className={`credit-bar cursor-pointer ${creditInfo.low_balance ? 'low-balance' : ''}`}
              onClick={() => setShowGlabsPanel(!showGlabsPanel)} data-testid="credit-balance-bar">
              <Gem className="w-4 h-4 text-[#D4A017]" />
              <span className="font-mono font-semibold text-[#D4A017]">{(creditInfo.glabs_balance || 0).toFixed(0)}</span>
              <span className="text-[10px] text-[#D4A017]">GLabs</span>
              <span className="text-[hsl(215,16%,30%)]">|</span>
              <Coins className="w-4 h-4" style={{ color: tierColor }} />
              <span className="font-mono font-semibold text-white">{(creditInfo.credits || 0).toFixed(0)}</span>
              <span className="text-[10px]" style={{ color: tierColor }}>Credits</span>
              {creditInfo.low_balance && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
              <span className={`tier-badge tier-${creditInfo.tier?.toLowerCase()}`} data-testid="tier-badge">
                {creditInfo.tier_name || creditInfo.tier}
              </span>
            </div>

            {/* GLabs Panel */}
            {showGlabsPanel && (
              <div className="absolute top-16 right-52 z-50 rounded-lg border border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)] shadow-xl w-80" data-testid="glabs-panel">
                {/* Tabs */}
                <div className="flex border-b border-[hsl(220,16%,18%)]">
                  {[{id:'buy',label:'Buy GLabs'},{id:'convert',label:'Convert Crypto'},{id:'credits',label:'GLabs → Credits'}].map(t => (
                    <button key={t.id} onClick={() => setPanelTab(t.id)}
                      className={`flex-1 py-2.5 text-xs font-medium transition-colors ${panelTab === t.id ? 'text-[#D4A017] border-b-2 border-[#D4A017]' : 'text-[hsl(215,16%,50%)] hover:text-white'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="p-4 space-y-3">
                  {/* Balances */}
                  <div className="flex items-center justify-between p-2 rounded bg-[hsl(220,16%,14%)]">
                    <div className="flex items-center gap-2">
                      <Gem className="w-3.5 h-3.5 text-[#D4A017]" />
                      <span className="text-xs text-[hsl(215,16%,56%)]">GLabs</span>
                    </div>
                    <span className="text-sm font-mono font-bold text-[#D4A017]">{(creditInfo.glabs_balance || 0).toFixed(0)}</span>
                  </div>

                  {panelTab === 'buy' && (
                    <>
                      <p className="text-[10px] text-[hsl(215,16%,45%)]">Buy GLabs with USDT at $0.25/GLabs</p>
                      <div>
                        <label className="text-xs text-[hsl(215,16%,56%)] mb-1 block">USDT Amount</label>
                        <input type="number" value={buyAmount} onChange={e => setBuyAmount(e.target.value)}
                          placeholder="e.g. 10" className="w-full p-2 rounded-md bg-[hsl(220,16%,16%)] border border-[hsl(220,16%,22%)] text-white text-sm"
                          data-testid="glabs-buy-amount" />
                      </div>
                      {buyAmount && parseFloat(buyAmount) > 0 && (
                        <p className="text-xs text-[#D4A017]">= {(parseFloat(buyAmount) / 0.25).toFixed(0)} GLabs</p>
                      )}
                      <Button onClick={handleBuyGlabs} disabled={actionLoading || !buyAmount}
                        className="w-full bg-[#D4A017] hover:bg-[#B8880F] text-white text-sm" data-testid="glabs-buy-btn">
                        <Gem className="w-3.5 h-3.5 mr-1" />
                        {actionLoading ? 'Processing...' : 'Buy GLabs'}
                      </Button>
                    </>
                  )}

                  {panelTab === 'convert' && (
                    <>
                      <p className="text-[10px] text-[hsl(215,16%,45%)]">Convert wallet crypto to GLabs</p>
                      <div>
                        <label className="text-xs text-[hsl(215,16%,56%)] mb-1 block">Currency</label>
                        <select value={convertCurrency} onChange={e => setConvertCurrency(e.target.value)}
                          className="w-full p-2 rounded-md bg-[hsl(220,16%,16%)] border border-[hsl(220,16%,22%)] text-white text-sm"
                          data-testid="glabs-convert-currency">
                          <option value="SOL">SOL ($150/SOL)</option>
                          <option value="USDC">USDC ($1/USDC)</option>
                          <option value="ETH">ETH ($3000/ETH)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-[hsl(215,16%,56%)] mb-1 block">Amount</label>
                        <input type="number" value={convertAmount} onChange={e => setConvertAmount(e.target.value)}
                          placeholder="e.g. 1" className="w-full p-2 rounded-md bg-[hsl(220,16%,16%)] border border-[hsl(220,16%,22%)] text-white text-sm"
                          data-testid="glabs-convert-amount" />
                      </div>
                      {convertAmount && parseFloat(convertAmount) > 0 && (
                        <p className="text-xs text-[#D4A017]">
                          = ${(parseFloat(convertAmount) * (cryptoRates[convertCurrency] || 1)).toFixed(2)} → {((parseFloat(convertAmount) * (cryptoRates[convertCurrency] || 1)) / 0.25).toFixed(0)} GLabs
                        </p>
                      )}
                      <Button onClick={handleConvertCrypto} disabled={actionLoading || !convertAmount}
                        className="w-full bg-[#00CED1] hover:bg-[#00B3B3] text-white text-sm" data-testid="glabs-convert-btn">
                        <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
                        {actionLoading ? 'Processing...' : 'Convert to GLabs'}
                      </Button>
                    </>
                  )}

                  {panelTab === 'credits' && (
                    <>
                      <p className="text-[10px] text-[hsl(215,16%,45%)]">1 GLabs = 1 Credit. Credits power bot execution.</p>
                      <div>
                        <label className="text-xs text-[hsl(215,16%,56%)] mb-1 block">GLabs to convert</label>
                        <input type="number" value={glabsToCredits} onChange={e => setGlabsToCredits(e.target.value)}
                          placeholder="e.g. 50" max={creditInfo.glabs_balance || 0}
                          className="w-full p-2 rounded-md bg-[hsl(220,16%,16%)] border border-[hsl(220,16%,22%)] text-white text-sm"
                          data-testid="glabs-to-credits-amount" />
                      </div>
                      {glabsToCredits && parseFloat(glabsToCredits) > 0 && (
                        <p className="text-xs text-[#E8520F]">= {parseFloat(glabsToCredits).toFixed(0)} Credits</p>
                      )}
                      <Button onClick={handleGlabsToCredits} disabled={actionLoading || !glabsToCredits}
                        className="w-full bg-[#E8520F] hover:bg-[#C9440C] text-white text-sm" data-testid="glabs-to-credits-btn">
                        <Coins className="w-3.5 h-3.5 mr-1" />
                        {actionLoading ? 'Processing...' : 'Convert to Credits'}
                      </Button>
                    </>
                  )}

                  {/* Credit Utilization Info */}
                  <div className="pt-2 border-t border-[hsl(220,16%,18%)] text-[10px] text-[hsl(215,16%,40%)] space-y-0.5">
                    <p>1 Credit per bot start | 1 Credit per $10 PnL | 5 Credits per withdrawal</p>
                    <p>GLabs rate: $0.25 USDT | Buy on any DeFi exchange</p>
                  </div>
                </div>
              </div>
            )}

            {/* Panic Stop */}
            <Button onClick={handlePanicStop} disabled={isPanicLoading}
              className={`panic-btn ${panicMode ? 'active' : ''}`} data-testid="panic-stop-btn">
              <AlertOctagon className="w-5 h-5" />
              <span>{panicMode ? 'RESET PANIC' : 'PANIC STOP'}</span>
            </Button>

            {/* Wallet Connect */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[hsl(220,16%,22%)] bg-transparent text-white hover:bg-[hsl(220,16%,16%)]"
                  data-testid="wallet-dropdown-btn">
                  <Wallet className="w-4 h-4 mr-2" /><span>Wallets</span><ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-[hsl(220,18%,11%)] border-[hsl(220,16%,18%)]">
                <div className="p-2">
                  <p className="text-xs text-[hsl(215,16%,56%)] uppercase tracking-wide mb-2">Connected Wallets</p>
                  <div className="flex items-center justify-between p-2 rounded-md bg-[hsl(220,16%,14%)] mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#AB9FF2] to-[#6366F1] flex items-center justify-center">
                        <span className="text-white text-xs font-bold">P</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Phantom</p>
                        <p className="text-xs text-[hsl(215,16%,56%)] font-mono">{wallets.solana ? formatAddress(wallets.solana.address) : 'Not connected'}</p>
                      </div>
                    </div>
                    {wallets.solana ? <Check className="w-4 h-4 text-[#10B981]" /> : <X className="w-4 h-4 text-[hsl(215,16%,40%)]" />}
                  </div>
                </div>
                <DropdownMenuSeparator className="bg-[hsl(220,16%,18%)]" />
                <DropdownMenuItem onClick={() => setShowWalletModal(true)}
                  className="cursor-pointer text-white hover:bg-[hsl(220,16%,16%)]" data-testid="connect-wallet-menu-item">
                  <Wallet className="w-4 h-4 mr-2" /> Manage Wallets
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[hsl(220,16%,22%)] bg-transparent text-white hover:bg-[hsl(220,16%,16%)]"
                  data-testid="user-menu-btn">
                  <User className="w-4 h-4 mr-2" /><span>{user?.name || 'User'}</span><ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[hsl(220,18%,11%)] border-[hsl(220,16%,18%)]">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-[hsl(215,16%,56%)]">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-[hsl(220,16%,18%)]" />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:bg-[hsl(220,16%,16%)]" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <WalletConnectModal open={showWalletModal} onClose={() => setShowWalletModal(false)} />
    </>
  );
}
